// src/components/Mesh.tsx
import { jsx } from "react/jsx-runtime";
function Mesh({ className, style }) {
  return /* @__PURE__ */ jsx(
    "div",
    {
      "aria-hidden": "true",
      className: className ? `vg-mesh ${className}` : "vg-mesh",
      style
    }
  );
}

// src/components/Grain.tsx
import { jsx as jsx2 } from "react/jsx-runtime";
function Grain({ opacity }) {
  if (opacity === void 0) {
    return /* @__PURE__ */ jsx2("div", { "aria-hidden": "true", className: "vg-grain" });
  }
  return /* @__PURE__ */ jsx2(
    "div",
    {
      "aria-hidden": "true",
      className: "vg-grain",
      style: { opacity }
    }
  );
}

// src/components/Reveal.tsx
import { useEffect, useRef } from "react";
import { jsx as jsx3 } from "react/jsx-runtime";
function Reveal({
  children,
  delay,
  threshold = 0.15,
  className
}) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") {
      el.classList.add("in");
      return;
    }
    const obs = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            el.classList.add("in");
            obs.disconnect();
            break;
          }
        }
      },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  const style = delay !== void 0 ? { transitionDelay: `${delay}ms` } : void 0;
  const cls = className ? `vg-reveal ${className}` : "vg-reveal";
  return /* @__PURE__ */ jsx3("div", { ref, "data-reveal": true, className: cls, ...style ? { style } : {}, children });
}

// src/components/SeverityBadge.tsx
import { Fragment, jsx as jsx4, jsxs } from "react/jsx-runtime";
var DEFAULT_LABEL = {
  block: "block",
  warn: "warn",
  info: "info",
  allow: "allow"
};
function SeverityBadge({
  severity,
  confidence,
  label,
  className
}) {
  const text = label ?? DEFAULT_LABEL[severity];
  const cls = className ? `vg-severity-badge vg-severity-badge--${severity} ${className}` : `vg-severity-badge vg-severity-badge--${severity}`;
  return /* @__PURE__ */ jsxs(
    "span",
    {
      className: cls,
      role: "status",
      "aria-label": `Severity: ${text}${confidence !== void 0 ? `, confidence ${confidence}` : ""}`,
      children: [
        /* @__PURE__ */ jsx4("span", { className: "vg-severity-badge__dot", "aria-hidden": "true" }),
        /* @__PURE__ */ jsx4("span", { className: "vg-severity-badge__label", children: text }),
        confidence !== void 0 ? /* @__PURE__ */ jsxs(Fragment, { children: [
          /* @__PURE__ */ jsx4("span", { className: "vg-severity-badge__sep", "aria-hidden": "true", children: "\xB7" }),
          /* @__PURE__ */ jsx4("span", { className: "vg-severity-badge__confidence", children: confidence })
        ] }) : null
      ]
    }
  );
}

// src/components/CatchCard.tsx
import { jsx as jsx5, jsxs as jsxs2 } from "react/jsx-runtime";
function CatchCard({
  code,
  title,
  severity,
  confidence,
  detail,
  fix,
  threatCategories,
  location,
  className,
  onActivate
}) {
  const cls = className ? `vg-catch-card vg-catch-card--${severity} ${className}` : `vg-catch-card vg-catch-card--${severity}`;
  const handleClick = onActivate ? () => onActivate() : void 0;
  const handleKey = onActivate ? (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onActivate();
    }
  } : void 0;
  const activationProps = onActivate ? {
    role: "button",
    tabIndex: 0,
    onClick: handleClick,
    onKeyDown: handleKey
  } : {};
  return /* @__PURE__ */ jsxs2("div", { className: cls, ...activationProps, children: [
    /* @__PURE__ */ jsxs2("header", { className: "vg-catch-card__head", children: [
      /* @__PURE__ */ jsxs2("div", { className: "vg-catch-card__id-block", children: [
        /* @__PURE__ */ jsx5("code", { className: "vg-catch-card__code", children: code }),
        /* @__PURE__ */ jsx5("h3", { className: "vg-catch-card__title", children: title })
      ] }),
      /* @__PURE__ */ jsx5(
        SeverityBadge,
        {
          severity,
          ...confidence !== void 0 ? { confidence } : {}
        }
      )
    ] }),
    location !== void 0 ? /* @__PURE__ */ jsx5("div", { className: "vg-catch-card__location", children: location }) : null,
    /* @__PURE__ */ jsx5("p", { className: "vg-catch-card__detail", children: detail }),
    fix !== void 0 ? /* @__PURE__ */ jsxs2("div", { className: "vg-catch-card__fix", children: [
      /* @__PURE__ */ jsx5("span", { className: "vg-catch-card__fix-label", children: "Fix" }),
      /* @__PURE__ */ jsx5("span", { className: "vg-catch-card__fix-text", children: fix })
    ] }) : null,
    threatCategories && threatCategories.length > 0 ? /* @__PURE__ */ jsx5("div", { className: "vg-catch-card__tags", children: threatCategories.map((cat) => /* @__PURE__ */ jsx5("span", { className: "vg-catch-card__tag", children: cat }, cat)) }) : null
  ] });
}

// src/components/ThemeToggle.tsx
import { useEffect as useEffect2, useState } from "react";
import { jsx as jsx6, jsxs as jsxs3 } from "react/jsx-runtime";
var STORAGE_DEFAULT = "vg-theme";
function readInitial(storageKey, defaultTheme) {
  if (storageKey === null) return defaultTheme;
  if (typeof window === "undefined") return defaultTheme;
  try {
    const stored = window.localStorage.getItem(storageKey);
    if (stored === "dark" || stored === "light") return stored;
  } catch {
  }
  return defaultTheme;
}
function ThemeToggle({
  target,
  storageKey = STORAGE_DEFAULT,
  defaultTheme = "dark",
  className,
  onChange
}) {
  const effectiveStorageKey = storageKey ?? null;
  const [theme, setTheme] = useState(
    () => readInitial(effectiveStorageKey, defaultTheme)
  );
  useEffect2(() => {
    const el = target ?? (typeof document !== "undefined" ? document.documentElement : null);
    if (!el) return;
    el.setAttribute("data-theme", theme);
    if (effectiveStorageKey !== null && typeof window !== "undefined") {
      try {
        window.localStorage.setItem(effectiveStorageKey, theme);
      } catch {
      }
    }
    onChange?.(theme);
  }, [theme, target, effectiveStorageKey, onChange]);
  const next = theme === "dark" ? "light" : "dark";
  const cls = className ? `vg-theme-toggle ${className}` : "vg-theme-toggle";
  return /* @__PURE__ */ jsx6(
    "button",
    {
      type: "button",
      className: cls,
      "aria-label": `Switch to ${next} theme`,
      title: `Switch to ${next} theme`,
      onClick: () => setTheme(next),
      "data-theme": theme,
      children: /* @__PURE__ */ jsx6("span", { className: "vg-theme-toggle__icon", "aria-hidden": "true", children: theme === "dark" ? (
        /* Sun glyph (we are in dark mode → click moves us to light → show sun) */
        /* @__PURE__ */ jsxs3("svg", { viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "1.6", strokeLinecap: "round", children: [
          /* @__PURE__ */ jsx6("circle", { cx: "12", cy: "12", r: "4" }),
          /* @__PURE__ */ jsx6("path", { d: "M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" })
        ] })
      ) : (
        /* Moon glyph */
        /* @__PURE__ */ jsx6("svg", { viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "1.6", strokeLinecap: "round", strokeLinejoin: "round", children: /* @__PURE__ */ jsx6("path", { d: "M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" }) })
      ) })
    }
  );
}

// src/components/CodeBlock.tsx
import { jsx as jsx7, jsxs as jsxs4 } from "react/jsx-runtime";
function CodeBlock({ code, language, className, style }) {
  const cls = className ? `vg-code-block ${className}` : "vg-code-block";
  return /* @__PURE__ */ jsxs4("div", { className: cls, ...style ? { style } : {}, children: [
    language ? /* @__PURE__ */ jsx7("header", { className: "vg-code-block__head", children: /* @__PURE__ */ jsx7("span", { className: "vg-code-block__lang", children: language }) }) : null,
    /* @__PURE__ */ jsx7("pre", { className: "vg-code-block__pre", children: /* @__PURE__ */ jsx7("code", { className: "vg-code-block__code", children: code }) })
  ] });
}

// src/components/Nav.tsx
import { jsx as jsx8, jsxs as jsxs5 } from "react/jsx-runtime";
function Nav({ brand, children, className }) {
  const cls = className ? `vg-nav ${className}` : "vg-nav";
  return /* @__PURE__ */ jsxs5("nav", { className: cls, children: [
    /* @__PURE__ */ jsx8("div", { className: "vg-nav__brand", children: brand }),
    /* @__PURE__ */ jsx8("div", { className: "vg-nav__actions", children })
  ] });
}
function NavCta({
  href,
  external,
  onClick,
  variant = "primary",
  className,
  children
}) {
  const cls = className ? `vg-nav-cta vg-nav-cta--${variant} ${className}` : `vg-nav-cta vg-nav-cta--${variant}`;
  if (href !== void 0) {
    const externalProps = external ? { target: "_blank", rel: "noopener noreferrer" } : {};
    return /* @__PURE__ */ jsx8("a", { className: cls, href, ...externalProps, children });
  }
  return /* @__PURE__ */ jsx8("button", { type: "button", className: cls, onClick, children });
}
export {
  CatchCard,
  CodeBlock,
  Grain,
  Mesh,
  Nav,
  NavCta,
  Reveal,
  SeverityBadge,
  ThemeToggle
};
//# sourceMappingURL=index.js.map